import { useEffect, useState } from "react";
import moment from "moment";
import { BiShow } from "react-icons/bi";
import { MdDelete, MdInfo, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { Box, IconButton, Icon, useDisclosure, Tooltip } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";

import { usePostOperations } from "../../features/admin/posts/hooks/usePostOperations";
import { usePostsList, Post } from "../../features/admin/posts/hooks/usePostsList";
import usePagination from "../../features/admin/usePagination";
import { Header, ModalBox } from "../../shared/components";
import { Table } from "../../features/admin/components";
import { PostModal } from "../../features/admin/posts/components";

function ViewPostsPage() {
  const [confirm, setConfirm] = useState<boolean>(false);
    const [postToDelete, setPostToDelete] = useState<string | null>(null);
    const [pagination, setPagination] = usePagination();
    const [selectedPost, setSelectedPost] = useState<Post | null>(null);
    const [confirmToggleVisibility, setConfirmToggleVisibility] = useState<boolean>(false);
    const [toggleVisibilityPost, setToggleVisibilityPost] = useState<Post | null>(null);
    const columnHelper = createColumnHelper<any>();

    const {
      isOpen: isDeleteOpen,
      onOpen: onDeleteOpen,
      onClose: onDeleteClose,
    } = useDisclosure();
    const {
      isOpen: isViewOpen,
      onOpen: onViewOpen,
      onClose: onViewClose,
    } = useDisclosure();
    const {
      isOpen: isToggleVisibilityOpen,
      onOpen: onToggleVisibilityOpen,
      onClose: onToggleVisibilityClose,
    } = useDisclosure();

    const { isLoading, data } = usePostsList(pagination);
    const { deletePost, toggleVisibility, isProcessing } = usePostOperations(pagination);
    const trimLongText = (text: string) => {
      return text.length > 80 ? `${text.substring(0, 80)}...` : text;
    };

    useEffect(() => {
        setPagination({
          ...pagination,
          currentPage: data?.data?.current_page ?? 1,
          perPage: pagination.perPage ?? 20,
          total: data?.data?.total_items ?? 0,
          totalPages: data?.data?.total_pages ?? 0,
        });
      
    }, [data]);

    useEffect(() => {
      if (confirm && postToDelete) {
        void deletePost(postToDelete);
        setConfirm(false);
        setPostToDelete(null);
        onDeleteClose();
      }
      if (confirmToggleVisibility && toggleVisibilityPost) {
        void toggleVisibility(toggleVisibilityPost);
        setConfirmToggleVisibility(false);
        setToggleVisibilityPost(null);
        onToggleVisibilityClose();
      }
    }, [confirm, confirmToggleVisibility, postToDelete, toggleVisibilityPost]);

    const onOpenModal = (post: Post) => {
      setSelectedPost(post);
      onViewOpen();
    };

    const handleToggleVisibility = (post: Post) => {
      setToggleVisibilityPost(post);
      onToggleVisibilityOpen();
    };

    const columns = [
      columnHelper.accessor("SN", {
        header: () => <span>SN</span>,
        size: 50,
        cell: (info) => (
          <span>
            {info.row.index + (pagination?.currentPage - 1) * pagination?.perPage + 1}
          </span>
        ),
      }),

      columnHelper.accessor("post_description", {
        id: "post_description",
        header: () => <span>Description</span>,
        size: 300,
        cell: (info) => <span>{trimLongText(info.getValue() || '')}</span>,
      }),

      columnHelper.accessor("type", {
        id: "type",
        header: () => <span>Type</span>,
        size: 100,
        cell: (info) => <span>{info.getValue()}</span>,
      }),

      columnHelper.accessor("updated_at", {
        id: "updated_at",
        header: () => <span>Updated At</span>,
        size: 200,
        cell: (info) => (
          <span>
            {moment(info.getValue()).format("MMMM D, YYYY")}
          </span>
        ),
      }),

      columnHelper.accessor("action", {
        header: () => <span>Action</span>,
        size: 200,
        cell: (info) => {
          const post = info.row.original;
          return (
            <span>
              <Tooltip label="View Post Details">
                <IconButton
                  aria-label="View Post Details"
                  icon={<Icon as={MdInfo}      w={5} h={5}  />}
                  onClick={() => onOpenModal(post)}
                  marginRight="4px"
                  isDisabled={isProcessing}
                />
              </Tooltip>
              <Tooltip label={post.is_hidden ? "Make Post Visible" : "Hide Post"}>
                <IconButton
                  aria-label="Toggle Visibility"
                  icon={
                    <Icon 
                      as={post.is_hidden ? MdVisibilityOff : MdVisibility} 
                      w={5} 
                      h={5} 
                      color={post.is_hidden ? "red" : "green"} 
                    />
                  }
                  onClick={() => handleToggleVisibility(post)}
                  marginRight="4px"
                  isDisabled={isProcessing}
                />
              </Tooltip>
              <Tooltip label="Delete Post">
                <IconButton
                  aria-label="Delete Post"
                  icon={<Icon as={MdDelete} w={5} h={5} color="red" />}
                  onClick={() => {
                    setPostToDelete(post.id);
                    onDeleteOpen();
                  }}
                  isDisabled={isProcessing}
                />
              </Tooltip>
            </span>
          );
        },
      }),
    ];

    return (
      <Box>
        <Header
          buttonIcon={null}
          header={"Post-list"}
          buttonText={undefined}
          handleOpen={() => {}}
        />
        <Table
          title="List of Posts"
          columns={columns}
          rowData={data?.data.data_list ?? []}
          pagination={pagination}
          setPagination={setPagination}
          loading={isLoading}
        />
        <PostModal
          isOpen={isViewOpen}
          post={selectedPost}
          onClose={onViewClose}
        />
        <ModalBox
          isOpen={isDeleteOpen}
          handleOpen={onDeleteOpen}
          handleClose={onDeleteClose}
          title="Delete Post"
          type="delete"
          confirm={confirm}
          setConfirm={setConfirm}
          size="sm"
        />
        <ModalBox
          isOpen={isToggleVisibilityOpen}
          handleOpen={onToggleVisibilityOpen}
          handleClose={onToggleVisibilityClose}
          title="Toggle Post Visibility"
          type="delete"
          confirm={confirmToggleVisibility}
          setConfirm={setConfirmToggleVisibility}
          size="sm"
        >
          {toggleVisibilityPost && (toggleVisibilityPost.is_hidden ? "Are you sure you want to make this post visible?" : "Are you sure you want to hide this post?")}
        </ModalBox>
      </Box>
    );
}

export default ViewPostsPage;
